import { useContext, useState } from 'react'

import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useIntl } from 'react-intl'

import { Link, Tooltip } from '@b-stock/fe-core/bstock-react'

import { ActionPanelContext } from '../ActionPanelProvider/ActionPanelContext'
import { CenteredFormRow, ErrorIcon, ErrorIconLink, ErrorText } from './ui'

/**
 * An error standing in for the form: no quote to bid against, no data, or a
 * request that failed.
 *
 * Collapsed, the message moves into a tooltip behind an error icon — a slot
 * this size has nowhere to put text, and text left in it pushes the action menu
 * off the line every other row holds.
 */
export const FormError = ({
  href,
  messageId,
}: {
  /** Present for the errors a buyer can act on by opening the listing. */
  href?: string
  messageId: string
}) => {
  const { formatMessage } = useIntl()
  const message = formatMessage({ id: messageId })

  // The tooltip positions against the icon, so it needs the element itself.
  const [element, setElement] = useState<HTMLElement | null>(null)

  // Optional: the Compare panel renders this outside the action panel.
  const variant = useContext(ActionPanelContext)?.variant

  if (variant !== 'narrow') {
    return href ? (
      <Link href={href}>{message}</Link>
    ) : (
      <ErrorText>{message}</ErrorText>
    )
  }

  return (
    <CenteredFormRow>
      {href ? (
        <ErrorIconLink aria-label={message} href={href} ref={setElement}>
          <FontAwesomeIcon icon={faExclamationCircle} />
        </ErrorIconLink>
      ) : (
        // Focusable so the tooltip is the error's only reachable form, not a
        // hover-only one.
        <ErrorIcon
          aria-label={message}
          ref={setElement}
          role="img"
          tabIndex={0}
        >
          <FontAwesomeIcon icon={faExclamationCircle} />
        </ErrorIcon>
      )}
      {/* Below, not beside: the panel gets narrower still at one menu icon. No
          containerElement, so it portals past the cell's clipping. */}
      <Tooltip placement="bottom" referenceElement={element}>
        {message}
      </Tooltip>
    </CenteredFormRow>
  )
}
